/** WELDING PROJECT PLANS™ — all customer-facing content and asset mapping. */

export interface PlanImage {
  src: string
  alt: string
  width: number
  height: number
}

export interface ProjectPage extends PlanImage {
  caption: string
}

export interface TechProof extends PlanImage {
  title: string
  project: string
  description: string
}

export interface Volume extends PlanImage {
  number: string
  title: string
  projects: string
  description: string
  /** Every project in the volume, as printed in the PDF index. */
  projectList: string[]
}

export interface Bonus extends PlanImage {
  number: string
  title: string
  description: string
}

export type SnapshotIcon = 'projects' | 'volumes' | 'dimensions' | 'bonus'

export interface SnapshotItem {
  label: string
  icon: SnapshotIcon
}

export interface Faq {
  question: string
  answer: string
}

/** Image host: light WebP files in the store's Impultienda resources. */
export const IMAGE_ORIGIN = 'https://aekpgsbqjzqntlbucbts.supabase.co'
const IMG = `${IMAGE_ORIGIN}/storage/v1/object/public/store-resources/79c12b18-58ee-4bd1-a643-dd3f199a1930/`

export const PRODUCT = {
  name: 'WELDING PROJECT PLANS™',
  descriptor: 'Workshop Equipment Build Plans for DIY Welders & Fabricators',
} as const

export const COMMERCIAL_RIGHTS = {
  label: 'COMMERCIAL BUILD RIGHTS INCLUDED',
  sentence: 'Build for your own workshop—or build and sell the finished products.',
} as const

export const OFFER_ANCHOR = '#offer'
export const PREVIEW_ANCHOR = '#project-preview'

export const TICKER_ITEMS: string[] = [
  '30 WORKSHOP PROJECTS',
  '6 DIGITAL VOLUMES',
  'DIMENSIONS & CUT LISTS',
  '5 FREE BONUS RESOURCES',
]

export const NAV_LINKS: Array<{ label: string; href: string }> = [
  { label: 'Preview', href: '#project-preview' },
  { label: 'Inside the Plans', href: '#technical-proof' },
  { label: 'Volumes', href: '#volumes' },
  { label: 'Bonuses', href: '#bonuses' },
  { label: 'FAQ', href: '#faq' },
]

const PAGE_SIZE = { width: 1530, height: 1980 }

export const HERO_MOCKUP: PlanImage = {
  src: `${IMG}1792699926066_landing-mockup-hero-85381001.webp`,
  alt: 'WELDING PROJECT PLANS™ collection: six numbered volumes, a tablet showing the P08 dimension plans, a printed cut-list sheet and a 5 free bonus badge',
  width: 1130,
  height: 1410,
}

export const SNAPSHOT: SnapshotItem[] = [
  { label: '30 Workshop Projects', icon: 'projects' },
  { label: '6 Organized Volumes', icon: 'volumes' },
  { label: 'Dimensions + Cut Lists', icon: 'dimensions' },
  { label: '5 Free Bonus Resources', icon: 'bonus' },
]

/** Project P26, Volume 05, PDF pages 29–34 — the complete project, in order. */
export const PREVIEW_PROJECT = {
  code: 'P26',
  name: 'Deburring & Surface Preparation Station',
  volume: 'Volume 05',
} as const

export const PROJECT_PAGES: ProjectPage[] = [
  {
    src: `${IMG}1792699931953_landing-p26-01-overview-9950e835.webp`,
    caption: 'The finished project',
    alt: 'Volume 05, page 29: P26 Deburring & Surface Preparation Station overview — all-steel solid-top finishing bench with rear splash plate and lower sheet shelf, W 36 in / 914.4 mm, D 24 in / 609.6 mm, H 42 in / 1066.8 mm',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792699937318_landing-p26-02-dimensioned-plans-ca5f1671.webp`,
    caption: 'Dimensioned plans — inches & millimeters',
    alt: 'Volume 05, page 30: P26 finished dimensions and orthographic plans — front, right, top and isometric views with outer envelope and internal elevation tables in inches and millimeters',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792699943015_landing-p26-03-cut-list-2f59aa21.webp`,
    caption: 'Materials, cut list & exploded components',
    alt: 'Volume 05, page 31: P26 cut list and materials table for parts A to F with stock and cut sizes in inches and millimeters, beside an exploded view of the components',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792699948698_landing-p26-04-fabrication-sequence-3c0e6ace.webp`,
    caption: 'Illustrated fabrication sequence',
    alt: 'Volume 05, page 32: P26 six-step illustrated fabrication sequence, from cut stock to the finished bench',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792699954238_landing-p26-05-joint-details-1aa48549.webp`,
    caption: 'Joint locations & fit-up details',
    alt: 'Volume 05, page 33: P26 joint location and physical fit-up details J1 to J4, with a dimensioned F-to-D joint sketch and clamp, tack, square, finish sequence',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792699961853_landing-p26-06-finished-reference-47a50884.webp`,
    caption: 'Finished fabrication reference',
    alt: 'Volume 05, page 34: P26 finished fabrication reference with overall dimensions, part counts A×4, B×4, C×4, D×1, E×1, F×1 and final-fit points',
    ...PAGE_SIZE,
  },
]

export const PLAN_BENEFITS: string[] = [
  'Plan the materials.',
  'See how the parts fit.',
  'Follow the fabrication sequence.',
]

const V01_PAGE_SIZE = { width: 918, height: 1188 }

export const TECH_PROOF: TechProof[] = [
  {
    src: `${IMG}1792699966738_landing-tech-01-trestle-dimensions-02603be0.webp`,
    title: 'KNOW THE DIMENSIONS',
    project: 'Project 02 · Fabrication Support Trestle Pair · Volume 01',
    description: 'Inspect the finished dimensions before cutting and assembly.',
    alt: 'Volume 01, page 10: Project 02 Fabrication Support Trestle Pair, finished dimensions of each trestle — 30 in wide, 18 in deep, 28.125 in high',
    ...V01_PAGE_SIZE,
  },
  {
    src: `${IMG}1792699989929_landing-tech-02-pedestal-cut-list-7faaefbf.webp`,
    title: 'KNOW WHAT TO CUT',
    project: 'Project 05 · Welding Lead & Torch Parking Pedestal · Volume 01',
    description: 'Review the parts, quantities, stock, and cut sizes.',
    alt: 'Volume 01, page 23: Project 05 Welding Lead & Torch Parking Pedestal, complete cut list for parts A to E with arm and lip detail',
    ...V01_PAGE_SIZE,
  },
  {
    src: `${IMG}1792699994635_landing-tech-03-roller-stand-5b71cf13.webp`,
    title: 'SEE HOW IT FITS',
    project: 'Project 03 · Adjustable Material Roller Stand · Volume 01',
    description: 'See the roller-head components and assembly details.',
    alt: 'Volume 01, page 34: Project 03 Adjustable Material Roller Stand, roller head assembly detail with brackets, roller and pin insertion',
    ...V01_PAGE_SIZE,
  },
  {
    src: `${IMG}1792699999884_landing-tech-04-screen-frame-sequence-7febeb67.webp`,
    title: 'FOLLOW THE BUILD',
    project: 'Project 04 · Freestanding Welding Screen Frame · Volume 01',
    description: 'Follow the illustrated sequence from cut parts to assembled frame.',
    alt: 'Volume 01, page 20: Project 04 Freestanding Welding Screen Frame, eight-step build sequence from cut parts to finished frame',
    ...V01_PAGE_SIZE,
  },
]

const COVER_SIZE = { width: 1254, height: 1254 }

export const VOLUMES: Volume[] = [
  {
    number: 'VOLUME 01',
    title: 'Workshop Essentials & Support Gear',
    projects: '6 projects',
    description: 'Pedestals, trestles, roller supports, and practical workshop support equipment.',
    projectList: [
      'P01 — Freestanding Bench Vise Pedestal',
      'P02 — Fabrication Support Trestle Pair',
      'P03 — Adjustable Material Roller Stand',
      'P04 — Freestanding Welding Screen Frame',
      'P05 — Welding Lead & Torch Parking Pedestal',
      'P06 — Workpiece Inspection & Rest Stand',
    ],
    src: `${IMG}1792700005161_landing-vol-1-aa42c869.webp`,
    alt: 'Volume 01 cover: Workshop Essentials & Support Gear',
    ...COVER_SIZE,
  },
  {
    number: 'VOLUME 02',
    title: 'Welding Tables & Workbenches',
    projects: '5 projects',
    description: 'Fixed welding tables and steel workbenches for fabrication.',
    projectList: [
      'P07 — Compact Fixed Welding Table',
      'P08 — Steel-Top Fabrication Workbench',
      'P09 — Slotted Welding Fixture Table',
      'P10 — Expandable Welding Workbench',
      'P11 — Corner Workshop Welding Bench',
    ],
    src: `${IMG}1792700010341_landing-vol-2-0e6050ef.webp`,
    alt: 'Volume 02 cover: Welding Tables & Workbenches',
    ...COVER_SIZE,
  },
  {
    number: 'VOLUME 03',
    title: 'Welding Carts & Mobile Stations',
    projects: '5 projects',
    description: 'Equipment carts and mobile stations for welding and preparation.',
    projectList: [
      'P12 — Compact Single-Machine Welding Cart',
      'P13 — Cylinder-Ready Multipurpose Welding Cart',
      'P14 — Dual-Machine Welding Equipment Cart',
      'P15 — Plasma Cutter Equipment Cart',
      'P16 — Mobile Fabrication Workstation',
    ],
    src: `${IMG}1792700016463_landing-vol-3-e3c19b0c.webp`,
    alt: 'Volume 03 cover: Welding Carts & Mobile Stations',
    ...COVER_SIZE,
  },
  {
    number: 'VOLUME 04',
    title: 'Storage & Material Racks',
    projects: '5 projects',
    description: 'Organize steel stock, sheet metal, offcuts, clamps, and hand tools.',
    projectList: [
      'P17 — Horizontal Steel Stock Rack',
      'P18 — Vertical Tube & Short-Stock Sorting Rack',
      'P19 — Sheet Metal Slot Storage Rack',
      'P20 — Steel Offcut Sorting Rack',
      'P21 — Wall-Mounted Clamp & Hand-Tool Rack',
    ],
    src: `${IMG}1792700022165_landing-vol-4-9db1268b.webp`,
    alt: 'Volume 04 cover: Storage & Material Racks',
    ...COVER_SIZE,
  },
  {
    number: 'VOLUME 05',
    title: 'Shop Tools & Preparation Stations',
    projects: '5 projects',
    description: 'Dedicated stations for cutting, grinding, drilling, layout, and finishing.',
    projectList: [
      'P22 — Metal Cut-Off Saw Workstation',
      'P23 — Bench Grinder Pedestal Stand',
      'P24 — Drill Press Support Workstation',
      'P25 — Metal Layout & Measuring Station',
      'P26 — Deburring & Surface Preparation Station',
    ],
    src: `${IMG}1792700027593_landing-vol-5-19bf4b5d.webp`,
    alt: 'Volume 05 cover: Shop Tools & Preparation Stations',
    ...COVER_SIZE,
  },
  {
    number: 'VOLUME 06',
    title: 'Integrated Workshop Systems',
    projects: '4 projects',
    description: 'Combine work zones, preparation, organization, and material handling.',
    projectList: [
      'P27 — Two-Zone Fabrication Island',
      'P28 — Wall-Length Workshop Service Station',
      'P29 — Cutting & Material Staging Line',
      'P30 — Portable Jobsite Fabrication Workstation',
    ],
    src: `${IMG}1792700047642_landing-vol-6-3f938542.webp`,
    alt: 'Volume 06 cover: Integrated Workshop Systems',
    ...COVER_SIZE,
  },
]

export const BONUSES: Bonus[] = [
  {
    number: '01',
    title: 'Steel Purchase & Project Worksheet',
    description: 'Organize your steel purchases and project notes.',
    src: `${IMG}1792700053189_landing-bonus-01-26d1f898.webp`,
    alt: 'Bonus 01 cover: Steel Purchase & Project Worksheet',
    ...COVER_SIZE,
  },
  {
    number: '02',
    title: 'Cut Optimization & Part Labeling Sheet',
    description: 'Plan your cutting layout and label parts before assembly.',
    src: `${IMG}1792700058648_landing-bonus-02-925089da.webp`,
    alt: 'Bonus 02 cover: Cut Optimization & Part Labeling Sheet',
    ...COVER_SIZE,
  },
  {
    number: '03',
    title: 'Workshop Safety Field Card',
    description: 'Keep a concise workshop checklist close at hand.',
    src: `${IMG}1792700064238_landing-bonus-03-b4f31103.webp`,
    alt: 'Bonus 03 cover: Workshop Safety Field Card',
    ...COVER_SIZE,
  },
  {
    number: '04',
    title: 'Surface Prep & Finishing Guide',
    description: 'Organize surface preparation and finishing steps.',
    src: `${IMG}1792700069756_landing-bonus-04-6c89c2df.webp`,
    alt: 'Bonus 04 cover: Surface Prep & Finishing Guide',
    ...COVER_SIZE,
  },
  {
    number: '05',
    title: 'Project Costing Worksheet',
    description: 'Track material costs and build your project estimate.',
    src: `${IMG}1792700075382_landing-bonus-05-9d8d9a51.webp`,
    alt: 'Bonus 05 cover: Project Costing Worksheet',
    ...COVER_SIZE,
  },
]

export const OFFER_FEATURES: string[] = [
  'Dimensions and technical views — inch-based plans with metric conversion references.',
  'Material and cut-list information',
  'Exploded views and fabrication guidance',
]

export const FAQS: Faq[] = [
  {
    question: 'What am I buying?',
    answer:
      'A digital collection of workshop equipment build plans: 30 projects across six PDF volumes, plus five additional resources.',
  },
  {
    question: 'Who is this collection for?',
    answer:
      'DIY welders with fabrication experience, independent fabricators, and small workshops. It is intended for people who already know how to weld and work with steel.',
  },
  {
    question: 'Is this a beginner welding course?',
    answer:
      'The collection focuses on workshop equipment projects and fabrication documentation. It does not teach welding from the beginning.',
  },
  {
    question: 'Are these physical books or equipment?',
    answer:
      'The product is digital. You receive PDF files; physical books, steel, tools, and equipment are not included.',
  },
  {
    question: 'Can I view the files on a tablet or computer?',
    answer:
      'Yes. You can open the PDFs on a compatible computer, tablet, or phone, and print the pages you need.',
  },
  {
    question: 'What kinds of projects are included?',
    answer:
      'Workshop support equipment, welding tables and workbenches, equipment carts, material racks, tool stations, and integrated workshop systems.',
  },
  {
    question: 'Can I preview the plans?',
    answer:
      'Yes. All six pages of Project P26 — Deburring & Surface Preparation Station, from Volume 05, are available in the preview section above.',
  },
  {
    question: 'Can I sell the products I build from these plans?',
    answer:
      'Yes. Your purchase includes permission to build projects from these plans for your own workshop or to sell the physical products you make. You may print working copies for your own use. You may not share, resell, redistribute, or sublicense the digital plans, PDFs, images, or bonus files, whether original or modified.',
  },
]

export const SUPPORT_EMAIL = 'atencion.creativodigital@gmail.com'

/** Single discreet guarantee line, shown only under the final purchase button. */
export const GUARANTEE_LINE = `7-day guarantee: if you have any problem with your purchase, email ${SUPPORT_EMAIL}.`
